import { HStack } from "@chakra-ui/react"
import DotBackground from "@pathwright/ui/src/components/background/DotBackground"
import ResourceBackground from "@pathwright/ui/src/components/background/ResourceBackground"
import Button from "@pathwright/ui/src/components/button/Button"
import useCardStackModal from "@pathwright/ui/src/components/card/useCardStackModal"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import PickerDropdown from "@pathwright/ui/src/components/picker/PickerDropdown"
import Text from "@pathwright/ui/src/components/ui/Text"
import TruncatedText from "@pathwright/ui/src/components/ui/TruncatedText"
import { BORDER_GRAY } from "@pathwright/ui/src/components/utils/colors"
import { useQuery } from "@pathwright/web/src/modules/utils/apollo"
import gql from "graphql-tag"
import get from "lodash/get"
import PropTypes from "prop-types"
import React, { useCallback, useState } from "react"
import styled from "styled-components"
import { hasEditorLevelAccess } from "../../group/permissions"
import SetupSourcePathCard from "../../path/source-path/SetupSourcePathCard"
import { usePathHasBeenPublished } from "../../path/sync/utils"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { getGroupUrl, getManageGroupsUrl } from "../../utils/urls"
import { SimpleCohortCard } from "../Cohorts"
import { CreateCohortCard } from "../create/CreateCohort"
import CohortSwitcherBlankSlate from "./CohortSwitcherBlankSlate"
import useCohortsQuery from "./useCohortsQuery"

const COHORT_QUERY = gql`
  query CohortSwitcherCohortQuery($id: Int!) {
    cohort: group(id: $id) {
      id
      name
      is_source_cohort
      resource {
        id
        slug
        licensed_from_school_id
        licensed_from_resource_id
        background_overlay
        background_image
        name
        blurred_background: background_image(blur: 500, width: 250, height: 250)
        source_cohort: group(type: curriculum) {
          id
        }
        resource_type
      }
      # for permissions
      user_registration {
        id
        role
      }
      user_source_registration {
        id
        role
      }
    }
  }
`

// Default PopoverTrigger child.
const CohortSwitcherTrigger = React.forwardRef(
  ({ onClick, title, icon }, ref) => (
    <Button
      ref={ref}
      styleType="primary"
      onClick={onClick}
      alt={title}
      style={{ maxWidth: "200px" }}
    >
      <Pathicon icon={icon} />
      <span> </span>
      <TruncatedText
        style={{ fontWeight: "bold", color: "white" }}
        tooltip={title}
      >
        {title}
      </TruncatedText>
      <span> </span>
      {!!onClick && <Pathicon icon="chevron-down" />}
    </Button>
  )
)

// Default method for attaching the Share popover to a component.
const attachCohortSwitcher = ({ renderTrigger }) =>
  renderTrigger(CohortSwitcherTrigger)

const CohortSwitcher = ({ cohortId, resourceId, section, children }) => {
  const [showingPicker, setShowingPicker] = useState(false)
  const pwContext = usePathwrightContext()
  const { t, tc } = useTranslate()

  const handleShowingPicker = useCallback(
    () => setShowingPicker(!showingPicker),
    [showingPicker]
  )

  const cohortQuery = useQuery(COHORT_QUERY, {
    variables: {
      id: cohortId
    }
  })

  const cohort = get(cohortQuery, "data.cohort")
  const isSourceCohort = cohort?.is_source_cohort

  const isEditor = hasEditorLevelAccess(pwContext, cohort)

  const { cohorts, loading } = useCohortsQuery({
    resourceId
  })

  // wrap useCohortsQuery to handle resourceId and return the pickerItems key that Picker expects
  const usePickerQuery = ({ search }) => {
    const { cohorts, ...rest } = useCohortsQuery({
      resourceId,
      search
    })
    return {
      pickerItems: cohorts,
      ...rest
    }
  }

  const sourcePathHasBeenPublished = usePathHasBeenPublished({
    cohortId: !isEditor
      ? undefined
      : isSourceCohort
      ? cohortId
      : cohortQuery?.data?.cohort?.resource?.source_cohort?.id
  })

  // The Card Stack opened by the "New Cohort" button depends on the resource's state:
  // - for Editors only
  // - if the resource has 1 cohort and the Source Path has never been published, then display the SetupSourcePathCard
  // - otherwise, show the CreateCohortCard
  const requireSetupSource =
    isEditor && !sourcePathHasBeenPublished && cohorts.length === 1

  const { cardStack, onOpenCardStack } = useCardStackModal({
    component: requireSetupSource ? SetupSourcePathCard : CreateCohortCard,
    props: requireSetupSource
      ? {
          cohort
        }
      : { resourceId }
  })

  if (!cohort) return null

  const switcherTitle = isSourceCohort ? t("Source Path") : cohort.name

  const isLicensed =
    cohort.resource.licensed_from_school_id &&
    cohort.resource.licensed_from_school_id !== pwContext.school.id

  // Show button if user is authenticated and has access to multiple cohorts OR user isEditor.
  const showSwitcher = Boolean(pwContext.me && (cohorts.length > 1 || isEditor))

  // Allow children as a function to take the place of the default attachShare.
  const attach = children || attachCohortSwitcher

  if (showSwitcher) {
    return (
      <>
        {cardStack}
        {
          <Container className="CohortSwitcher">
            {attach({
              renderTrigger: (CohortSwitcherTrigger, props) => (
                <CohortSwitcherTrigger
                  title={switcherTitle}
                  icon={isSourceCohort ? "cloud-arrows-fill" : "group"}
                  onClick={handleShowingPicker}
                  {...props}
                />
              )
            })}
            {showingPicker && (
              <StyledPickerDropdown
                // NOTE: onOutsideClick closes the picker when clicking the Button above
                // which creates the effect of the picker reopening when clicking said button =/
                onOutsideClick={() => setShowingPicker(false)}
                prompt={null}
                searchPrompt={cohorts.length >= 5 ? t("Search cohorts") : null}
                usePickerQuery={usePickerQuery}
                onPick={(item) => {
                  if (window.App) {
                    setShowingPicker(false)
                    window.App.navigate(
                      getGroupUrl(cohort.resource.slug, item.id, section)
                    )
                  }
                }}
                renderPickerItem={({ pickerItem, pickerItems, index }) => {
                  const zIndex = 1 + pickerItems.length - index
                  return (
                    <StyledSimpleCohortCard item={pickerItem} zIndex={zIndex} />
                  )
                }}
              >
                {({ pickerItems, pickerList, search }) => (
                  <>
                    <StyledHeader>
                      <Text.H5
                        color="#8a8a8a"
                        style={{ flexGrow: 1, textAlign: "left" }}
                      >
                        {t("$t(cohort_plural, capitalize)")}
                      </Text.H5>
                      {isEditor && (
                        <>
                          <Button
                            styleType="blank"
                            to={getManageGroupsUrl(cohort.resource.id)}
                            onClick={() => setShowingPicker(false)}
                            color="#8a8a8a"
                          >
                            <Pathicon icon="gear" />
                            <span> </span>
                            {tc("manage")}
                          </Button>

                          <Button
                            styleType="blank"
                            onClick={() => {
                              setShowingPicker(false)
                              if (!isLicensed) onOpenCardStack()
                            }}
                            to={
                              isLicensed
                                ? `/library/store/${cohort.resource.licensed_from_school_id}/${cohort.resource.licensed_from_resource_id}/`
                                : null
                            }
                            color="#8a8a8a"
                          >
                            <Pathicon icon="group-plus" />
                            <span> </span>
                            {tc("new")}
                          </Button>
                        </>
                      )}
                    </StyledHeader>
                    <div style={{ minHeight: "50px" }}>
                      {pickerItems.length ? (
                        pickerList
                      ) : loading ? (
                        <div
                          style={{ minHeight: "50px", position: "relative" }}
                        >
                          <LoadingCircle center />
                        </div>
                      ) : (
                        <CohortSwitcherBlankSlate search={search} />
                      )}
                    </div>
                    {!isSourceCohort &&
                      // ensure there is a source cohort to switch to!
                      // (current workaround for licensed courses that do not have their own source path)
                      !!cohort.resource.source_cohort &&
                      hasEditorLevelAccess(pwContext, cohort) && (
                        <SourceButton
                          styleType="blank"
                          inverted
                          to={getGroupUrl(
                            cohort.resource.slug,
                            cohort.resource.source_cohort.id,
                            section
                          )}
                          onClick={() => setShowingPicker(false)}
                        >
                          <div>
                            <HStack justify="start" gap={1} align="center">
                              <Pathicon
                                icon="cloud-arrows-fill"
                                style={{
                                  marginRight: "0",
                                  marginBottom: "-2px",
                                  height: "1.2em",
                                  width: "1.2em"
                                }}
                              />
                              <strong>{t("Switch to Source Path")}</strong>
                            </HStack>
                            <div>{t("Share updates with all Cohorts")}</div>
                          </div>
                          <ResourceBackground
                            resource={cohort.resource}
                            forceBlur
                          />
                          <DotBackground dotSize={4} dotSpacing={24} />
                        </SourceButton>
                      )}
                  </>
                )}
              </StyledPickerDropdown>
            )}
          </Container>
        }
      </>
    )
  }

  // Only show simple cohort name.
  return (
    <Container className="CohortSwitcher">
      {attach({
        renderTrigger: (CohortSwitcherTrigger, props) => (
          <CohortSwitcherTrigger
            title={cohort.name}
            icon={"group"}
            {...props}
          />
        )
      })}
    </Container>
  )
}

CohortSwitcher.displayName = "CohortSwitcher"

CohortSwitcher.propTypes = {
  cohortId: PropTypes.number.isRequired,
  resourceId: PropTypes.number.isRequired,
  section: PropTypes.oneOf(["about", "path", "community"]).isRequired
}

CohortSwitcher.defaultProps = {
  section: "path",
  cohortId: null,
  resourceId: null
}

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  .pathicon-cloud-arrows-fill {
    margin-bottom: -4px;
  }
`

const StyledPickerDropdown = styled(PickerDropdown)`
  ul.PickerList li.PickerListItem {
    /* for custom picker item component, remove 40px height */
    height: initial;
    margin-top: 5px;
  }

  ul.PickerList li.PickerListItem:hover {
    background-color: initial;
  }

  & .Fullscreen__maximize {
    display: none;
  }
`

const SourceButton = styled(Button)`
  margin-top: 1em;

  .Button__content-wrapper {
    justify-content: flex-start;

    i {
      margin-right: 1em;
    }

    > *:not(.BackgroundImage):not(.DotBackground) {
      text-align: left;
      /* layer text above background image */
      position: relative;
      z-index: 1;
    }
  }

  /* forcing background image to fill padding gutters, obviously 10px hardcoded isn't great */
  .BackgroundImage,
  .DotBackground {
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    position: absolute;
    width: initial;
    height: initial;
  }
`

const StyledSimpleCohortCard = styled(SimpleCohortCard)`
  width: 100%;
  max-width: initial;
  min-height: initial;

  .CohortMembers:empty {
    height: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .CohortCard__background {
    height: initial;
  }
`

const StyledHeader = styled.header`
  display: flex;
  align-items: center;
  border-bottom: 2px solid ${BORDER_GRAY};

  h5 {
    flex-grow: 1;
  }
`

export default CohortSwitcher
